import React from 'react';
import { FiClock, FiMoreHorizontal, FiArrowRight } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { t } from 'i18next';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { confirm } from '@/lib/confirm';
import usePermission from '@/hooks/usePermission';
import { Dropdown } from '@/components/custom/dropdown/dropdown';
import { LiveClassCard, LiveClassCardBody, LiveClassCardFooter, LiveClassCardHeader, LiveClassCardHeaderInner, LiveClassCardTime, LiveClassCardTitle } from '@/components/custom/live-class';
import { Badge } from '@/components/ui/badge';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';

function resolveDateTime(item) {
  const startDateTime = item['initial-time'] ? moment(`${item['initial-date']} ${item['initial-time']}`, 'YYYY-MM-DD HH:mm') : moment(item['initial-date']);

  const endDateTime = item['end-time'] ? moment(`${item['initial-date']} ${item['end-time']}`, 'YYYY-MM-DD HH:mm') : moment(item['end-date'] || item['initial-date']);

  return { startDateTime, endDateTime };
}

const accessButtonClassName = cn(
  'inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full px-2 h-6 text-xs font-bold text-primary-foreground transition-colors duration-150 ease-out-strong',
  'bg-primary hover:bg-primary-dark',
  'active:scale-[0.97]'
);

function LiveClassSidebarCard({ liveClass, setEditingLiveClass, setShowLiveClassForm, setCurrentLive, setWeekDays }) {
  const { startDateTime, endDateTime } = resolveDateTime(liveClass);
  const date = `${startDateTime.format('HH[h]mm')} às ${endDateTime.format('HH[h]mm')}`;
  const isOnSchedule = moment().isBetween(startDateTime, endDateTime);
  const isStudentPlayful = usePlayfulStudent();
  const navigate = useNavigate();
  const client = useClient();

  const deleteLive = async () => {
    const { error } = await client.delete(`live_classrooms/${liveClass?.id}`);
    if (error) {
      toast.error(t('toast.errorDeleteLesson'));
    } else {
      setWeekDays(prevState => prevState.filter(item => item.id !== liveClass?.id));
      toast.success(t('toast.successDeleteLesson'));
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteLesson'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteLive();
    }
  };

  return (
    <LiveClassCard
      className={`${isOnSchedule ? 'cursor-pointer' : ''} gap-3`}
      onClick={() => {
        if (isOnSchedule) {
          navigate(`/aulas-ao-vivo/${liveClass.id}`);
        }
      }}
    >
      <LiveClassCardHeader>
        <LiveClassCardHeaderInner>
          <Badge
            variant={isOnSchedule ? 'success' : 'neutral'}
            size="xs"
          >
            Aula ao vivo - {isOnSchedule ? 'Online' : 'Offline'}
          </Badge>

          {usePermission('Editar aulas ao vivo', 'Aulas ao vivo') || usePermission('Excluir aulas ao vivo', 'Aulas ao vivo') ? (
            <div onClick={e => e.stopPropagation()}>
              <Dropdown>
                <Dropdown.Toggle>
                  <span className="card__icon-wrapper u-py-0">
                    <FiMoreHorizontal className="card__icon" />
                  </span>
                </Dropdown.Toggle>

                <Dropdown.Content>
                  {usePermission('Editar aulas ao vivo', 'Aulas ao vivo') && (
                    <Dropdown.Item
                      onClick={() => {
                        setEditingLiveClass(true);
                        setShowLiveClassForm(true);
                        setCurrentLive(liveClass);
                      }}
                    >
                      {t('button.edit')}
                    </Dropdown.Item>
                  )}
                  {usePermission('Excluir aulas ao vivo', 'Aulas ao vivo') && <Dropdown.Item onClick={handleDelete}>{t('button.delete')}</Dropdown.Item>}
                </Dropdown.Content>
              </Dropdown>
            </div>
          ) : null}
        </LiveClassCardHeaderInner>

        <LiveClassCardTitle title={liveClass.title}>{liveClass.title}</LiveClassCardTitle>
      </LiveClassCardHeader>

      <LiveClassCardBody className="flex items-center justify-between gap-2">
        <LiveClassCardTime title={date}>
          <FiClock /> {date}
        </LiveClassCardTime>
        {isOnSchedule && isStudentPlayful && (
          <Link
            to={`/aulas-ao-vivo/${liveClass.id}`}
            aria-label={t('button.access')}
            onClick={e => e.stopPropagation()}
            className="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-full text-white transition-transform duration-100 hover:translate-x-0.5"
            style={{
              background: 'var(--color-success)',
              boxShadow: '0 3px 0 color-mix(in srgb, var(--color-third) 72%, #000), 0 4px 8px rgba(0,0,0,0.18)'
            }}
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-1/2"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.28) 0%, transparent 100%)' }}
            />
            <FiArrowRight className="relative h-4 w-4" />
          </Link>
        )}
      </LiveClassCardBody>
      {isOnSchedule && !isStudentPlayful && (
        <LiveClassCardFooter>
          <Link
            to={`/aulas-ao-vivo/${liveClass.id}`}
            className={accessButtonClassName}
          >
            {t('button.access')}
          </Link>
        </LiveClassCardFooter>
      )}
    </LiveClassCard>
  );
}

export { LiveClassSidebarCard };
